import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { PageHero } from '../components/PageHero';
import { TruckIcon, ClipboardCheckIcon, MapPinIcon, PhoneIcon, ClockIcon, CheckCircle2Icon, CalendarIcon, ShieldCheckIcon, StethoscopeIcon, FileTextIcon } from 'lucide-react';
const locations = [{
  name: 'Landover Location',
  address: '8700 Central Ave #302A, Landover, MD 20785',
  phone: '(301) 613-9295',
  hours: 'Mon–Fri: 8:00 AM – 6:00 PM | Sat: 9:00 AM – 2:00 PM'
}, {
  name: 'Oxon Hill Location',
  address: '6188 Oxon Hill Rd #601, Oxon Hill, MD 20745',
  phone: '(240) 553-7993',
  hours: 'Mon–Fri: 8:00 AM – 7:00 PM | Sat: 9:00 AM – 2:00 PM'
}];
const examIncludes = ['Vision acuity test (Snellen chart)', 'Blood pressure and pulse rate check', 'Urinalysis for glucose, protein, and specific gravity', 'Hearing assessment (forced whisper test)', 'Physical examination of eyes, ears, throat, heart, lungs, and abdomen', 'Neurological assessment and musculoskeletal evaluation', 'Review of medical history and current medications', 'Completion of FMCSA medical examiner certificate'];
const whatToBring = ["Valid government-issued photo ID (driver's license or passport)", 'List of all current medications with dosages', 'Glasses or contact lenses (if you wear them for driving)', 'Hearing aids (if applicable)', 'CPAP/BiPAP compliance report (if you use a sleep apnea device)', 'Letter from your specialist (if you have diabetes, heart conditions, or seizure history)'];
const containerVariants = {
  hidden: {
    opacity: 0
  },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.08
    }
  }
};
const itemVariants = {
  hidden: {
    opacity: 0,
    y: 16
  },
  show: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.4,
      ease: 'easeOut'
    }
  }
};
export function DotPhysicalPage() {
  return <>
      <PageHero eyebrow="FMCSA Certified Examiners" title="DOT Physical Examinations" subtitle="Fast, affordable, and FMCSA-compliant DOT physicals at both our Landover and Oxon Hill locations. Walk-ins welcome — most exams completed in under 30 minutes." bgImage="https://images.unsplash.com/photo-1601584115197-04ecc0da31d7?w=1920&q=80" data-id="element-409" />

      {/* Key Benefits Strip */}
      <div className="bg-[#0A71B1] text-white" data-id="element-410">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-411">
          <div className="grid grid-cols-1 sm:grid-cols-3 divide-y sm:divide-y-0 sm:divide-x divide-white/20" data-id="element-412">
            {[{
            icon: <ClockIcon className="h-6 w-6" data-id="element-413" />,
            text: 'Results in Under 30 Minutes'
          }, {
            icon: <ShieldCheckIcon className="h-6 w-6" data-id="element-414" />,
            text: 'FMCSA Certified Examiners'
          }, {
            icon: <TruckIcon className="h-6 w-6" data-id="element-415" />,
            text: 'Walk-Ins Welcome'
          }].map(item => <div key={item.text} className="flex items-center justify-center gap-3 py-5 px-4" data-id="element-416">
                {item.icon}
                <span className="text-sm md:text-base font-semibold" data-id="element-417">
                  {item.text}
                </span>
              </div>)}
          </div>
        </div>
      </div>

      {/* What is a DOT Physical */}
      <section className="py-16 md:py-24 bg-white" aria-labelledby="what-is-dot" data-id="element-418">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-419">
          <div className="flex flex-col lg:flex-row gap-12 lg:gap-20 items-center" data-id="element-420">
            <motion.div className="w-full lg:w-1/2" initial={{
            opacity: 0,
            x: -30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true,
            margin: '-80px'
          }} transition={{
            duration: 0.6
          }} data-id="element-421">
              <div className="relative rounded-[10px] overflow-hidden" data-id="element-422">
                <img src="https://images.unsplash.com/photo-1612349317150-e413f6a5b16d?w=800&q=80" alt="DOT physical examination at Healthy Choice Family Clinic" className="w-full h-auto object-cover" data-id="element-423" />
              </div>
            </motion.div>

            <motion.div className="w-full lg:w-1/2" initial={{
            opacity: 0,
            x: 30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true,
            margin: '-80px'
          }} transition={{
            duration: 0.6,
            delay: 0.15
          }} data-id="element-424">
              <p className="text-sm font-bold tracking-[0.2em] uppercase text-teal-500 mb-3" data-id="element-425">
                About the Exam
              </p>
              <h2 id="what-is-dot" className="text-2xl md:text-3xl lg:text-4xl font-bold text-navy-500 leading-snug mb-6" data-id="element-426">
                What Is a DOT Physical?
              </h2>
              <p className="text-base md:text-lg text-gray-500 leading-relaxed mb-4" data-id="element-427">
                A DOT (Department of Transportation) physical is a medical
                examination required by the Federal Motor Carrier Safety
                Administration (FMCSA) for commercial motor vehicle (CMV)
                drivers. It ensures that drivers are physically and mentally fit
                to safely operate commercial vehicles.
              </p>
              <p className="text-base md:text-lg text-gray-500 leading-relaxed mb-6" data-id="element-428">
                At Healthy Choice Family Clinic, our FMCSA-certified medical
                examiners perform DOT physicals at{' '}
                <strong data-id="element-429">both our Landover and Oxon Hill locations</strong>. We
                make the process fast, convenient, and hassle-free — with most
                exams completed in under 30 minutes.
              </p>
              <a href="#schedule-dot" className="inline-flex items-center justify-center bg-[#E8891D] hover:bg-[#d07a15] text-white px-8 py-3.5 rounded-full font-semibold text-base transition-colors min-h-[48px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#E8891D] focus-visible:ring-offset-2" data-id="element-430">
                Schedule Your DOT Physical
              </a>
            </motion.div>
          </div>
        </div>
      </section>

      {/* What the Exam Includes */}
      <section className="py-16 md:py-24 bg-gray-50" aria-labelledby="exam-includes" data-id="element-431">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-432">
          <div className="text-center mb-12" data-id="element-433">
            <div className="inline-flex items-center justify-center h-14 w-14 rounded-[10px] bg-[#0A71B1] text-white mb-5" data-id="element-434">
              <StethoscopeIcon className="h-7 w-7" data-id="element-435" />
            </div>
            <h2 id="exam-includes" className="text-2xl md:text-3xl font-bold text-navy-500 mb-4" data-id="element-436">
              What the DOT Physical Includes
            </h2>
            <p className="text-base text-gray-500 max-w-2xl mx-auto" data-id="element-437">
              Our comprehensive exam follows all FMCSA guidelines to ensure you
              meet federal requirements.
            </p>
          </div>

          <motion.div className="grid grid-cols-1 sm:grid-cols-2 gap-4" variants={containerVariants} initial="hidden" whileInView="show" viewport={{
          once: true,
          margin: '-60px'
        }} data-id="element-438">
            {examIncludes.map(item => <motion.div key={item} variants={itemVariants} className="flex items-start gap-3 bg-white border border-[#E5E9F0] rounded-[10px] p-5" data-id="element-439">
                <CheckCircle2Icon className="h-5 w-5 text-teal-500 flex-shrink-0 mt-0.5" data-id="element-440" />
                <span className="text-sm md:text-base text-navy-500 leading-snug" data-id="element-441">
                  {item}
                </span>
              </motion.div>)}
          </motion.div>
        </div>
      </section>

      {/* What to Bring */}
      <section className="py-16 md:py-24 bg-white" aria-labelledby="what-to-bring" data-id="element-442">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-443">
          <div className="text-center mb-12" data-id="element-444">
            <div className="inline-flex items-center justify-center h-14 w-14 rounded-[10px] bg-[#0A71B1] text-white mb-5" data-id="element-445">
              <FileTextIcon className="h-7 w-7" data-id="element-446" />
            </div>
            <h2 id="what-to-bring" className="text-2xl md:text-3xl font-bold text-navy-500 mb-4" data-id="element-447">
              What to Bring to Your Appointment
            </h2>
            <p className="text-base text-gray-500 max-w-2xl mx-auto" data-id="element-448">
              Come prepared to make your visit as quick and smooth as possible.
            </p>
          </div>

          <motion.div className="grid grid-cols-1 sm:grid-cols-2 gap-4" variants={containerVariants} initial="hidden" whileInView="show" viewport={{
          once: true,
          margin: '-60px'
        }} data-id="element-449">
            {whatToBring.map(item => <motion.div key={item} variants={itemVariants} className="flex items-start gap-3 bg-gray-50 border border-[#E5E9F0] rounded-[10px] p-5" data-id="element-450">
                <ClipboardCheckIcon className="h-5 w-5 text-[#E8891D] flex-shrink-0 mt-0.5" data-id="element-451" />
                <span className="text-sm md:text-base text-navy-500 leading-snug" data-id="element-452">
                  {item}
                </span>
              </motion.div>)}
          </motion.div>
        </div>
      </section>

      {/* Locations */}
      <section className="py-16 md:py-24 bg-gray-50" aria-labelledby="dot-locations" id="schedule-dot" data-id="element-453">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-454">
          <div className="text-center mb-12" data-id="element-455">
            <h2 id="dot-locations" className="text-2xl md:text-3xl font-bold text-navy-500 mb-4" data-id="element-456">
              Available at Both Locations
            </h2>
            <p className="text-base text-gray-500 max-w-2xl mx-auto" data-id="element-457">
              Walk in or call ahead to schedule your DOT physical at whichever
              location is most convenient.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-458">
            {locations.map(loc => <motion.div key={loc.name} className="bg-white border border-[#E5E9F0] rounded-[10px] p-8" initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5
          }} data-id="element-459">
                <h3 className="text-xl font-bold text-navy-500 mb-4" data-id="element-460">
                  {loc.name}
                </h3>
                <div className="space-y-3 mb-6" data-id="element-461">
                  <div className="flex items-start gap-3 text-gray-500" data-id="element-462">
                    <MapPinIcon className="h-5 w-5 text-[#0A71B1] flex-shrink-0 mt-0.5" data-id="element-463" />
                    <span className="text-base" data-id="element-464">{loc.address}</span>
                  </div>
                  <div className="flex items-center gap-3 text-gray-500" data-id="element-465">
                    <PhoneIcon className="h-5 w-5 text-[#0A71B1] flex-shrink-0" data-id="element-466" />
                    <a href={`tel:${loc.phone.replace(/\D/g, '')}`} className="text-base text-[#0A71B1] font-semibold hover:underline" data-id="element-467">
                      {loc.phone}
                    </a>
                  </div>
                  <div className="flex items-start gap-3 text-gray-500" data-id="element-468">
                    <ClockIcon className="h-5 w-5 text-[#0A71B1] flex-shrink-0 mt-0.5" data-id="element-469" />
                    <span className="text-sm" data-id="element-470">{loc.hours}</span>
                  </div>
                </div>
                <a href={`tel:${loc.phone.replace(/\D/g, '')}`} className="inline-flex items-center justify-center w-full bg-[#0A71B1] hover:bg-[#085d91] text-white py-3.5 rounded-[10px] font-semibold text-base transition-colors min-h-[48px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0A71B1] focus-visible:ring-offset-2" data-id="element-471">
                  <CalendarIcon className="h-5 w-5 mr-2" data-id="element-472" />
                  Schedule at {loc.name.split(' ')[0]}
                </a>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* FAQ / Bottom CTA */}
      <section className="py-16 md:py-24 bg-[#0A71B1]" data-id="element-473">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-474">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} data-id="element-475">
            <TruckIcon className="h-12 w-12 text-white/80 mx-auto mb-6" strokeWidth={1.5} data-id="element-476" />
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-4" data-id="element-477">
              Get Back on the Road — Fast
            </h2>
            <p className="text-base md:text-lg text-white/80 mb-4 max-w-xl mx-auto leading-relaxed" data-id="element-478">
              DOT physicals are valid for up to 24 months. Our certified
              examiners will have you in and out quickly so you can focus on
              what matters — your job.
            </p>
            <p className="text-sm text-white/60 mb-10" data-id="element-479">
              No appointment necessary. Walk-ins accepted at both locations
              during business hours.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-480">
              <a href="tel:3016139295" className="inline-flex items-center justify-center bg-[#E8891D] hover:bg-[#d07a15] text-white px-8 py-4 rounded-full font-semibold text-lg transition-colors min-h-[48px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-[#0A71B1]" data-id="element-481">
                Call Landover: (301) 613-9295
              </a>
              <a href="tel:2405537993" className="inline-flex items-center justify-center border-2 border-white/40 hover:bg-white/10 text-white px-8 py-4 rounded-full font-semibold text-lg transition-colors min-h-[48px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white" data-id="element-482">
                Call Oxon Hill: (240) 553-7993
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </>;
}